/**
 * SettingsMenu — the menu behind the settings gear (§7.9): app update status
 * and actions, the microphone permission check, and the Feedback, Install and
 * Sign out entries. The shell mounts it only while the menu is open.
 */

import { useEffect, useState } from "preact/hooks";
import {
  classifyMicrophoneFailure,
  microphoneRecoveryCopy,
  queryMicrophonePermission,
  requestMicrophoneStream,
  type MicrophonePermissionState,
} from "../media/microphone-access";
import type { UpdateStatus } from "../shell/use-update-channel";
import { Chip } from "./Chip";
import { MenuRow } from "./MenuRow";
import { Modal } from "./Modal";

export interface SettingsMenuProps {
  bundleName: string;
  updateStatus: UpdateStatus;
  updateReady: boolean;
  onCheckUpdate: () => void;
  onApplyUpdate: () => void;
  onClose: () => void;
  onFeedback: () => void;
  onInstallGuide: () => void;
  /** Omitted when there is no cookie session to end, which hides the row. */
  onSignOut?: () => void;
}

export function SettingsMenu({
  bundleName,
  updateStatus,
  updateReady,
  onCheckUpdate,
  onApplyUpdate,
  onClose,
  onFeedback,
  onInstallGuide,
  onSignOut,
}: SettingsMenuProps) {
  const [settingsMicrophone, setSettingsMicrophone] = useState<MicrophonePermissionState>("checking");

  useEffect(() => {
    let mounted = true;
    void queryMicrophonePermission().then((state) => {
      if (mounted) setSettingsMicrophone(state);
    });
    return () => { mounted = false; };
  }, []);

  async function checkSettingsMicrophone() {
    setSettingsMicrophone("checking");
    try {
      const stream = await requestMicrophoneStream();
      if (typeof stream.getTracks === "function") stream.getTracks().forEach((track) => track.stop());
      setSettingsMicrophone("granted");
    } catch (error) {
      setSettingsMicrophone(classifyMicrophoneFailure(error));
    }
  }

  return (
    <Modal
      testid="settings-menu"
      position="absolute"
      align="top-end"
      zIndex={20}
      scrim={55}
      // Opens just below the gear, which sits below the status bar.
      padding="calc(48px + max(4px, env(safe-area-inset-top, 0px))) 16px 0"
      onClose={onClose}
      card={{ width: "260px", padding: "8px 20px", shadow: true }}
    >
      <div data-testid="settings-update-panel" style={{ padding: "12px 0 14px" }}>
        <p style={{ margin: "0 0 6px", fontSize: "13px", fontWeight: 700, color: "var(--mark-text-primary)" }}>
          App update
        </p>
        <p data-testid="settings-bundle-version" style={{ margin: "0 0 8px", fontSize: "11.5px", color: "var(--mark-text-muted)" }}>
          Running {bundleName}
        </p>
        <p data-testid="settings-update-status" style={{ margin: "0 0 10px", fontSize: "11.5px", color: "var(--mark-text-muted)" }}>
          {updateStatus === "checking"
            ? "Checking for an update…"
            : updateStatus === "ready"
              ? "A new version is ready."
              : updateStatus === "applying"
                ? "Applying update…"
                : updateStatus === "activating"
                  ? "The new version is activating. This page will reload when it takes control."
                : updateStatus === "blocked"
                  ? "Finish or save the open capture before updating."
                  : updateStatus === "failed"
                    ? "The update check failed. Try again when you have signal."
                    : updateStatus === "unsupported"
                      ? "Updates aren't available in this browser."
                      : updateStatus === "current"
                        ? "This is the current version."
                        : "Check before a pilot session if the app looks stale."}
        </p>
        <div style={{ display: "flex", gap: "8px" }}>
          <Chip
            data-testid="settings-check-update"
            disabled={updateStatus === "checking" || updateStatus === "applying" || updateStatus === "activating"}
            onClick={onCheckUpdate}
          >
            Check for update
          </Chip>
          <Chip
            data-testid="settings-apply-update"
            disabled={!updateReady || updateStatus === "applying"}
            onClick={onApplyUpdate}
          >
            Update now
          </Chip>
        </div>
      </div>
      <div
        data-testid="settings-microphone-panel"
        style={{ padding: "12px 0 14px", borderTop: "1px solid var(--mark-bg-surface-alt)" }}
      >
        <p style={{ margin: "0 0 6px", fontSize: "13px", fontWeight: 700, color: "var(--mark-text-primary)" }}>
          Microphone permissions
        </p>
        <p data-testid="settings-microphone-status" style={{ margin: "0 0 8px", fontSize: "11.5px", color: "var(--mark-text-muted)" }}>
          {settingsMicrophone === "granted"
            ? "Microphone access is ready."
            : settingsMicrophone === "checking"
              ? "Checking microphone access…"
              : microphoneRecoveryCopy()}
        </p>
        <Chip
          data-testid="settings-check-microphone"
          disabled={settingsMicrophone === "checking"}
          onClick={() => void checkSettingsMicrophone()}
        >
          {settingsMicrophone === "granted" ? "Check again" : "Try microphone access"}
        </Chip>
      </div>
      {/* Testids UNCHANGED from the retired nav entries (§7.9): only the
          entry points move behind the gear; the mounted components and
          their own testids are untouched. */}
      <MenuRow chevron data-testid="nav-tab-feedback" onClick={onFeedback}>
        Send feedback / report a bug
      </MenuRow>
      <MenuRow divider chevron data-testid="nav-install-guide" onClick={onInstallGuide}>
        Add Mark to your home screen
      </MenuRow>
      {onSignOut && (
        <MenuRow divider chevron data-testid="settings-sign-out" onClick={onSignOut}>
          Sign out
        </MenuRow>
      )}
    </Modal>
  );
}
